import PropTypes from "prop-types";
import { ReduceStore } from "flux/utils";
import Dispatcher from "Dispatcher";
import { hotLoadObject } from "data/storeUtils";
import FormActions from "./FormActions";
import LoadObject from "data/LoadObject";
import dotProp from "dot-prop-immutable";
import DataManager from "data/DataManager";
import userStore from "data/userStore";
import EntityName from "data/util/EntityName";
import entityMetaStore from "data/meta/entityMetaStore";
import NavigationActions from "../NavigationActions";

let id_seq = 0;

const BASE_TIMESTAMP_DESCRIPTOR = {
    _synthetic: true,
    type: "Widget",
    control: "timestamp",
    disableBehaviour: "hide",
    config: {
        mask: "MM/dd/yyyy HH:mm",
    },
};

const hasConfigColumns = it => {
    const isCompSummary = (it.type === "CompositionField" && it.control === "summarysection");
    const isCompDLG = (it.type === "CompositionField" && it.control === "devicelesiongrid");
    const isAssocDLG = (it.type === "AssociationField" && it.control === "devicelesiongrid");
    const isSummaryOrDLG = isCompSummary || isCompDLG || isAssocDLG;
    return isSummaryOrDLG && it.hasOwnProperty("config") && it.config.hasOwnProperty("columns");
};

const assignIds = it => {
    if (it.id == null) {
        it.id = "fi" + (++id_seq);
    }
    if (it.childItems != null) {
        for (const c of it.childItems) {
            assignIds(c);
        }
    }
    if (hasConfigColumns(it)) {
        for (const c of it.config.columns) {
            c.hasOwnProperty("itemInfo") && assignIds(c.itemInfo);
        }
    }
};

const RE_FIELD_TYPE = /.*Field$/;
const convertToViewOnly = it => {
    if (it.childItems != null) {
        for (const c of it.childItems) {
            convertToViewOnly(c);
        }
    }
    if (hasConfigColumns(it)) {
        for (const c of it.config.columns) {
            if (c.type === "edit") c.type = "view";
            c.hasOwnProperty("itemInfo") && convertToViewOnly(c.itemInfo);
        }
    } else if (RE_FIELD_TYPE.test(it.type) && it.control !== "label") {
        it.control = "label";
    }
};

const itemTypes = {
    id: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    control: PropTypes.string.isRequired,
    config: PropTypes.object,
    childItems: PropTypes.array, // of PropTypes.shape(itemTypes)
};

// it's a little weird to have a display name here in a store, but the way the
// jump-to menu was implemented, it uses display names as keys. thus while this
// _is_ a display name, it's semantics in this context are "section key". one of
// the smaller problems in the data model. :|
export const IMPLICIT_TOP_OF_FORM_SECTION_NAME = "Top of Form";

export const extractSectionList = descriptor => {
    const names = extractSectionListHelper(descriptor);
    if (names.length > 1) {
        // only see about adding the implicit top-of-form item if there is more
        // than one explicit section on the form.
        const desc = descriptor.childItems[0];
        if (desc.type !== "Layout" || desc.control !== "section") {
            // there is stuff above the form's first section
            names.unshift(IMPLICIT_TOP_OF_FORM_SECTION_NAME);
        }
    }
    return names;
};

const extractSectionListHelper = desc => {
    const names = [];
    if (desc.type === "Layout" && desc.control === "section") {
        names.push(desc.label);
    }
    if (desc.hasOwnProperty("childItems")) {
        for (const it of desc.childItems) {
            names.push(...extractSectionListHelper(it));
        }
    }
    return names;
};

const firstNonSyntheticChild = desc =>
    desc.childItems.find(it => !it._synthetic);

export const firstItemInSection = (desc, sectionName) => {
    if (sectionName === IMPLICIT_TOP_OF_FORM_SECTION_NAME) {
        return firstNonSyntheticChild(desc);
    } else {
        return firstItemInSectionHelper(desc, sectionName);
    }
};

const firstItemInSectionHelper = (desc, sectionName) => {
    if (desc.type === "Layout" && desc.control === "section" && desc.label === sectionName) {
        // this one!
        return firstNonSyntheticChild(desc);
    }
    if (desc.hasOwnProperty("childItems")) {
        for (const it of desc.childItems) {
            const item = firstItemInSectionHelper(it, sectionName);
            if (item != null) {
                return item;
            }
        }
    }
    return null;
};

class FormStore extends ReduceStore {

    constructor() {
        super(Dispatcher);
    }

    getInitialState() {
        return {};
    }

    reduce(state, action) {
        switch (action.type) {

            case NavigationActions.SELECT_RECORD: {
                const key = action.entityKey;
                if (!state[key]) return state;
                const lo = state[key];
                if (lo.hasOperation() || lo.hasValue() || !lo.hasError()) return state;
                // it has exactly an error; clear it to try again
                return {
                    ...state,
                    [key]: lo.removeError(),
                };
            }

            case FormActions.START_LOAD: {
                this.__dispatcher.waitFor([
                    userStore.getDispatchToken(),
                ]);
                const lo = state[action.key] || LoadObject.empty().withPropTypes(itemTypes);
                return dotProp.set(state, action.key, lo.loading(
                    DataManager.loadFormDescriptor(
                        action.key,
                        userStore.getActiveOrgKey()
                    )
                ));
            }

            case FormActions.LOADED: {
                this.__dispatcher.waitFor([
                    entityMetaStore.getDispatchToken(),
                ]);
                action.data.sectionNames = extractSectionList(action.data);
                const implKey = new EntityName(action.key).implKey;
                if (userStore.getLicensePreference("record-timestamps-on-form", implKey)) {
                    action.data.childItems.unshift({
                        ...BASE_TIMESTAMP_DESCRIPTOR,
                        key: "ao_created_at",
                        label: "Record Created",
                    }, {
                        ...BASE_TIMESTAMP_DESCRIPTOR,
                        key: "ao_updated_at",
                        label: "Record Updated",
                    });
                }
                if (!entityMetaStore.getInfo(action.key).canWrite) {
                    convertToViewOnly(action.data);
                }
                assignIds(action.data);
                return dotProp.set(state, action.key, lo => lo.receiveValue(action.data));
            }

            case FormActions.LOAD_ERROR:
                return dotProp.set(state, action.key, lo => lo.receiveError(action.error));

            default:
                return state;
        }
    }

    getFirstItemInSection(key, sectionName) {
        return firstItemInSection(this.getForm(key), sectionName);
    }

    getSectionNames(key) {
        return this.getForm(key).sectionNames;
    }

    getForm(key) {
        return this.getFormLO(key).getRequiredValue();
    }

    getFormLO(key) {
        return hotLoadObject(
            () => this.getState()[key],
            () => Dispatcher.dispatch({
                type: FormActions.START_LOAD,
                key
            }),
        );
    }

    getNextControl(entityKey, fieldKey, fieldOnly = false) {
        const form = this.getForm(entityKey);
        let returnNext = false;
        const work = layout => {
            for (const kid of layout.childItems) {
                if (returnNext && (fieldOnly ? kid.type.endsWith("Field") : kid.type !== "Layout")) {
                    return kid;
                }
                if (kid.key === fieldKey) {
                    returnNext = true;
                    continue;
                }
                if (kid.childItems) {
                    const result = work(kid);
                    if (result != null) {
                        return result;
                    }
                }
            }
        };
        const next = work(form);
        return next == null ? null : next;
    }
}

export default new FormStore();



// WEBPACK FOOTER //
// ./app/data/meta/FormStore.js